import { useRef, useEffect } from 'react';
import {
  doc,
  query,
  where,
  orderBy,
  updateDoc,
  onSnapshot,
  collection,
  Unsubscribe,
  getFirestore,
  serverTimestamp,
} from 'firebase/firestore';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { SystemRequestType } from 'src/utils/guards/system-request-types';

import useUserDataStore from 'src/store/user';
import { useSystemStore } from 'src/store/SystemStore';
import { useCompanionStore } from 'src/store/CompanionStore';

import useAuthStore from '../store/firebase/auth-store';
import { getFirebaseWebApp } from '../store/firebase/lib';
import { requireFirebaseClaims } from '../store/firebase/auth-session';

const DB = getFirestore(getFirebaseWebApp());

interface SystemRequest {
  targetedDeviceId: string;
  isAcknowledged: boolean;
  type: SystemRequestType;
  createdAt?: any;
  acknowledgedAt?: any;
}

type Props = {
  children: React.ReactNode;
};

export default function SystemRequestGuard({ children }: Props) {
  const router = useRouter();
  const { authenticated, logout } = useAuthStore();
  const { business } = useUserDataStore();
  const { currentDevice } = useSystemStore();
  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const { deviceData } = useCompanionStore();

  useEffect(() => {
    // Determine which device ID to use: companion device or regular device
    const activeDeviceId = deviceData?.id || currentDevice?.id;

    console.log('SystemRequestGuard - Checking conditions:', {
      authenticated,
      businessId: business?.id,
      activeDeviceId,
      deviceData: deviceData?.id,
      currentDevice: currentDevice?.id
    });

    // Check if we have the minimum requirements to listen
    if (!authenticated || !business?.id || !activeDeviceId) {
      console.log('SystemRequestGuard - Missing requirements, not listening');
      return undefined;
    }

    const businessId = business.id;
    const deviceId = activeDeviceId;

    console.log(`SystemRequestGuard - Starting listener for device: ${deviceId} in business: ${businessId}`);

    const listenToSystemRequests = async () => {
      // Gate: verify the web SDK token has businessId before subscribing to the protected path
      const session = await requireFirebaseClaims(['businessId']);
      if (!session) {
        console.warn('[SystemRequestGuard] businessId claim missing in web SDK token — skipping listener. Check web SDK auth sync.');
        return;
      }

      const systemRequestsRef = collection(DB, `business/${businessId}/system-requests`);
      const requestQuery = query(
        systemRequestsRef,
        where('targetedDeviceId', '==', deviceId),
        where('isAcknowledged', '==', false),
        orderBy('createdAt', 'asc')
      );

      console.log(`SystemRequestGuard - Query created for device: ${deviceId}`);

      unsubscribeRef.current = onSnapshot(
        requestQuery,
        async (snapshot) => {
          console.log(`SystemRequestGuard - Snapshot received, ${snapshot.docs.length} unacknowledged requests found`);

          if (snapshot.docs.length === 0) {
            return;
          }

          // Process each unacknowledged request
          const processRequests = snapshot.docs.map(async (docSnapshot) => {
            const requestData = docSnapshot.data() as SystemRequest;
            const requestRef = doc(DB, `business/${businessId}/system-requests/${docSnapshot.id}`);

            try {
              // First acknowledge the request
              await updateDoc(requestRef, {
                isAcknowledged: true,
                acknowledgedAt: serverTimestamp(),
              });

              console.log(`System request acknowledged: ${requestData.type}`);

              // Then perform the action
              switch (requestData.type) {
                case SystemRequestType.SYSTEM_REFRESH:
                  console.log('Performing system refresh...');
                  router.push(paths.auth.login);

                  window.location.reload();
                  break;

                case SystemRequestType.SYSTEM_LOGOUT:
                  console.log('Performing system logout...');
                  await logout();
                  router.push(paths.auth.login);
                  break;

                case SystemRequestType.SYSTEM_DELETE:
                  console.log('Performing system delete - logging out...');
                  // Just logout, the requesting device will delete the record
                  await logout();
                  router.push(paths.auth.login);
                  break;

                default:
                  console.warn(`Unknown system request type: ${requestData.type}`);
              }
            } catch (error) {
              console.error('Error processing system request:', error);
            }
          });

          // Process all requests
          await Promise.all(processRequests);
        },
        (error) => {
          console.error('Error listening to system requests:', error);
        }
      );
    };

    listenToSystemRequests();

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, business?.id, currentDevice?.id, deviceData?.id, logout, router]);

  return <>{children}</>;
}
